// @ts-nocheck
// @ts-ignore
import { Component } from '@theme/component';

export class RecentlyViewedProducts extends Component {
  #STORAGE_KEY = 'recentlyViewedVariants';
  #MAX_VARIANTS = 25;
  #MIN_PRODUCTS_TO_SHOW = 3;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  #initialize() {
    const container = this.refs.container || this.querySelector('[ref="container"]') || this.querySelector('.recently-viewed-products__list') || this.querySelector('.swiper-wrapper');
    if (!container) {
      setTimeout(() => this.#initialize(), 10);
      return;
    }

    if (!this.refs.container && container) {
      this.refs.container = container;
    }

    const variantIds = this.#getStoredVariants();
    const currentVariantId = this.dataset.currentVariantId;

    if (!variantIds || variantIds.length < this.#MIN_PRODUCTS_TO_SHOW) {
      this.#hideSection();
      return;
    }

    const filteredVariants = currentVariantId
      ? variantIds.filter(id => id !== currentVariantId)
      : variantIds;

    if (filteredVariants.length < this.#MIN_PRODUCTS_TO_SHOW) {
      this.#hideSection();
      return;
    }

    this.#fetchProducts(filteredVariants);
  }

  #getStoredVariants() {
    const stored = localStorage.getItem(this.#STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  /**
   * Add or remove centered class when there are exactly 3 slides and viewport is desktop.
   * @param {HTMLElement} container - The list container (.recently-viewed-products__list / .swiper-wrapper)
   * @param {number} slideCount - Number of slides
   */
  #updateCenteredClass(container, slideCount) {
    if (!container) return;
    const shouldCenter = slideCount === 3 && window.innerWidthCached >= 1024;
    if (shouldCenter) {
      container.classList.add('recently-viewed-products__list--centered');
    } else {
      container.classList.remove('recently-viewed-products__list--centered');
    }
  }

  async #fetchProducts(variantIds) {
    const graphqlEndpoint = this.dataset.graphqlEndpoint;
    const accessToken = window.storefrontAccessToken;

    if (!graphqlEndpoint || !accessToken) {
      this.#hideSection();
      return;
    }

    try {
      const query = this.#buildGraphQLQuery(variantIds);
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken,
        },
        body: JSON.stringify({ query }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(data.errors[0]?.message || 'GraphQL error');
      }

      const products = this.#processGraphQLResponse(data.data, variantIds);

      if (products.length < this.#MIN_PRODUCTS_TO_SHOW) {
        this.#hideSection();
        return;
      }

      this.#renderProducts(products);
    } catch (error) {
      this.#hideSection();
    }
  }

  #buildGraphQLQuery(variantIds) {
    const nodes = variantIds
      .slice(0, this.#MAX_VARIANTS)
      .map((id, index) => {
        const gid = `gid://shopify/ProductVariant/${id}`;
        return `node${index}: node(id: "${gid}") {
          ... on ProductVariant {
            id
            title
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
            }
            availableForSale
            product {
              id
              title
              handle
              tags
              featuredImage {
                url
                altText
                width
                height
              }
              metafields(identifiers: [{namespace: "neo", key: "highlights"}, {namespace: "neo", key: "usage_tag"}]) {
                namespace
                key
                value
              }
            }
          }
        }`;
      })
      .join('\n');

    return `{
      ${nodes}
    }`;
  }

  #processGraphQLResponse(data, variantIds) {
    const products = [];
    const nodeKeys = Object.keys(data).filter(key => key.startsWith('node'));
    const seenProductIds = new Set();

    for (const key of nodeKeys) {
      const variant = data[key];
      if (!variant || !variant.product) continue;

      const product = variant.product;
      const productId = product.id.split('/').pop();
      if (seenProductIds.has(productId)) continue;
      seenProductIds.add(productId);
      let highlightsValue = '';
      let tagValue = '';
      let newImageTag = '';
      let saleImageTag = '';
      if (product?.tags?.join(' ').toLowerCase().includes('new 2026')) {
        newImageTag = 'NEW';
      }
      if (product?.tags?.join(' ').toLowerCase().includes('sale 2026')) {
        saleImageTag = 'SALE';
      }

      if (product.metafields && product.metafields.length > 0) {
        const highlights = product.metafields.find(m => m?.namespace === 'neo' && m?.key === 'highlights');
        if (highlights?.value) {
          try {
            const highlightsData = typeof highlights.value === 'string' ? JSON.parse(highlights.value) : highlights.value;
            if (Array.isArray(highlightsData)) {
              highlightsValue = highlightsData.join(' | ');
            } else {
              highlightsValue = highlights.value;
            }
          } catch {
            highlightsValue = highlights.value;
          }
        }

        const tag = product.metafields.find(m => m?.namespace === 'neo' && m?.key === 'tag');
        if (tag?.value) {
          tagValue = tag.value;
        }
      }

      products.push({
        variantId: variant.id.split('/').pop(),
        productId: product.id.split('/').pop(),
        title: product.title,
        handle: product.handle,
        price: parseFloat(variant.price.amount),
        compareAtPrice: parseFloat(variant.compareAtPrice?.amount || 0),
        currencyCode: variant.price.currencyCode,
        availableForSale: variant.availableForSale,
        image: product.featuredImage,
        highlights: highlightsValue,
        tag: tagValue,
        imageTag: newImageTag,
        saleImageTag: saleImageTag
      });
    }

    return products.filter(p => p.availableForSale);
  }

  #renderProducts(products) {
    let container = this.refs.container;
    if (!container || Array.isArray(container)) {
      const foundContainer = this.querySelector('[ref="container"]') || this.querySelector('.recently-viewed-products__list') || this.querySelector('.swiper-wrapper');
      if (foundContainer && !Array.isArray(foundContainer)) {
        container = foundContainer;
      }
    }
    if (!container || Array.isArray(container)) {
      console.error('RecentlyViewedProducts: Container not found');
      return;
    }

    const containerEl = /** @type {HTMLElement} */ (container);

    const fragment = document.createDocumentFragment();
    products.forEach((product) => {
      const card = this.#createProductCard(product);
      const slide = document.createElement('div');
      slide.className = 'swiper-slide';
      slide.appendChild(card);
      fragment.appendChild(slide);
    });

    containerEl.innerHTML = '';
    containerEl.appendChild(fragment);
    this.style.display = 'block';

    this.#updateCenteredClass(containerEl, products.length);

    // Reload SmartWishlist SDK after products are rendered
    if (typeof ReloadSmartWishlist === 'function') {
      setTimeout(() => {
        ReloadSmartWishlist();
      }, 300);
    }

    this.#initializeSwiper();
  }

  #initializeSwiper() {
    const swiperComponent = /** @type {any} */ (this.querySelector('swiper-component'));
    if (swiperComponent) {
      // Destroy existing swiper if it exists
      if (swiperComponent.swiper) {
        swiperComponent.swiper.destroy(true, true);
        swiperComponent.swiper = null;
      }

      // Wait for DOM to be ready and images to load
      setTimeout(() => {
        if (swiperComponent.swiper === null && typeof swiperComponent.init === 'function') {
          swiperComponent.init();
          if (swiperComponent.swiper) {
            const swiper = swiperComponent.swiper;

            // Set parameters
            swiper.params.spaceBetween = 14;
            swiper.params.watchOverflow = true;
            swiper.params.slidesPerGroup = 1;
            swiper.params.resistance = true;
            swiper.params.resistanceRatio = 0;
            swiper.params.allowTouchMove = true;

            if (swiper.params.breakpoints) {
              Object.keys(swiper.params.breakpoints).forEach(bp => {
                if (swiper.params.breakpoints[bp]) {
                  swiper.params.breakpoints[bp].spaceBetween = 14;
                }
              });
            }

            // Override pagination render to show correct number of bullets
            const slides = swiper.slides;
            const totalSlides = slides.length;
            const slidesPerView = swiper.params.slidesPerView;

            if (totalSlides > slidesPerView) {
              const maxIndex = Math.max(0, totalSlides - Math.ceil(slidesPerView));
              const totalBullets = maxIndex + 1;

              // Override pagination renderBullet to limit the number of bullets
              swiper.params.pagination.renderBullet = function (/** @type {number} */ index, /** @type {string} */ className) {
                // Only render bullets up to totalBullets
                if (index >= totalBullets) {
                  return '';
                }
                return '<span class="' + className + '"></span>';
              };

              // Update pagination
              if (swiper.pagination && swiper.pagination.render) {
                swiper.pagination.render();
                swiper.pagination.update();
              }
            }

            // Update swiper
            swiper.update();
            swiper.updateSlides();
            swiper.updateSlidesClasses();
            swiper.updateSize();
            swiper.updateSlidesClasses();

            // Force recalculation after a short delay to ensure images are loaded
            setTimeout(() => {
              swiper.update();
              swiper.updateSlides();
              swiper.updateSlidesClasses();
              swiper.updateSize();

              // Ensure we're at the beginning
              swiper.slideTo(0, 0);

              // Calculate the correct number of pagination bullets and update
              const updatePagination = () => {
                const slides = swiper.slides;
                const totalSlides = slides.length;
                const slidesPerView = swiper.params.slidesPerView;

                if (totalSlides <= slidesPerView) {
                  // All slides fit, hide pagination
                  const paginationEl = swiper.pagination.el;
                  if (paginationEl) {
                    paginationEl.style.display = 'none';
                  }
                  return;
                }

                // Calculate maximum navigable index
                const maxIndex = Math.max(0, totalSlides - Math.ceil(slidesPerView));
                // Number of pagination bullets = number of navigable positions = maxIndex + 1
                const totalBullets = maxIndex + 1;

                // Get current pagination bullets
                const paginationEl = swiper.pagination.el;
                if (!paginationEl) return;

                paginationEl.style.display = 'block';
                const currentBullets = paginationEl.querySelectorAll('.swiper-pagination-bullet');
                const currentCount = currentBullets.length;

                // If we have the wrong number of bullets, force Swiper to re-render
                if (currentCount !== totalBullets) {
                  // Re-render pagination with the correct renderBullet function
                  if (swiper.pagination && swiper.pagination.render) {
                    swiper.pagination.render();
                    swiper.pagination.update();
                  }
                } else {
                  // Just update active state
                  const activeIndex = Math.min(swiper.activeIndex, maxIndex);
                  const bullets = paginationEl.querySelectorAll('.swiper-pagination-bullet');
                  bullets.forEach((/** @type {HTMLElement} */ bullet, /** @type {number} */ index) => {
                    if (index === activeIndex) {
                      bullet.classList.add('swiper-pagination-bullet-active');
                    } else {
                      bullet.classList.remove('swiper-pagination-bullet-active');
                    }
                  });
                }
              };

              // Calculate max translate using Swiper's native calculation
              const getMaxTranslate = () => {
                // Swiper uses negative values for translate (slides move left)
                // The max translate is the most negative value we can go
                const slides = swiper.slides;
                const totalSlides = slides.length;
                const slidesPerView = swiper.params.slidesPerView;

                if (totalSlides <= slidesPerView) {
                  return 0; // All slides fit, no scrolling needed
                }

                // Use Swiper's internal calculation
                // Max translate = -(total width of slides - visible width)
                const wrapper = swiper.wrapperEl;
                const containerWidth = swiper.width;
                const wrapperWidth = wrapper.scrollWidth;

                // Calculate the maximum translate (negative value)
                const maxTranslate = -(wrapperWidth - containerWidth);

                return maxTranslate;
              };

              // Override setTranslate to clamp the value
              const originalSetTranslate = swiper.setTranslate.bind(swiper);
              swiper.setTranslate = function (/** @type {number} */ translate) {
                const maxTranslate = getMaxTranslate();
                // Clamp translate: it should be between maxTranslate (most negative) and 0
                const clampedTranslate = Math.max(maxTranslate, Math.min(0, translate));
                originalSetTranslate(clampedTranslate);
              };

              // Override setWrapperTranslate to prevent going past the end.
              // Guard for Swiper versions where this internal method is unavailable.
              if (typeof swiper.setWrapperTranslate === 'function') {
                const originalSetWrapperTranslate = swiper.setWrapperTranslate.bind(swiper);
                swiper.setWrapperTranslate = function (/** @type {number} */ translate) {
                  const maxTranslate = getMaxTranslate();
                  const clampedTranslate = Math.max(maxTranslate, Math.min(0, translate));
                  originalSetWrapperTranslate(clampedTranslate);
                };
              }

              // Update pagination after initialization
              updatePagination();

              // Add event listener to prevent scrolling past the last slide
              swiper.on('slideChange', () => {
                const slides = swiper.slides;
                const currentIndex = swiper.activeIndex;
                const slidesPerView = swiper.params.slidesPerView;
                const totalSlides = slides.length;

                if (totalSlides <= slidesPerView) {
                  updatePagination();
                  return; // All slides visible, no need to check
                }

                // Calculate if we're at or past the last valid slide
                const maxIndex = Math.max(0, totalSlides - Math.ceil(slidesPerView));

                // If we've scrolled past the last valid position, snap back
                if (currentIndex > maxIndex) {
                  swiper.slideTo(maxIndex, 300);
                }

                // Update pagination to reflect current position
                updatePagination();
              });

              // Prevent touch from going past the end during drag
              swiper.on('touchMove', () => {
                const maxTranslate = getMaxTranslate();
                if (swiper.translate < maxTranslate) {
                  swiper.setTranslate(maxTranslate);
                }
              });

              // Also prevent on touch end
              swiper.on('touchEnd', () => {
                const maxTranslate = getMaxTranslate();
                if (swiper.translate < maxTranslate) {
                  swiper.setTranslate(maxTranslate);
                  swiper.updateSlidesClasses();
                }
              });

              // Prevent navigation button from going past the end
              const originalSlideNext = swiper.slideNext.bind(swiper);
              swiper.slideNext = function () {
                const slides = swiper.slides;
                const slidesPerView = swiper.params.slidesPerView;
                const totalSlides = slides.length;
                const maxIndex = Math.max(0, totalSlides - Math.ceil(slidesPerView));

                if (swiper.activeIndex < maxIndex) {
                  originalSlideNext();
                }
              };

              // Handle resize to recalculate
              let /** @type {ReturnType<typeof setTimeout> | undefined} */ resizeTimeout;
              const handleResize = () => {
                if (resizeTimeout) clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                  swiper.update();
                  swiper.updateSlides();
                  swiper.updateSlidesClasses();
                  swiper.updateSize();

                  // Center list when 3 slides and width >= 1024px
                  this.#updateCenteredClass(swiper.wrapperEl, swiper.slides?.length ?? 0);

                  // Ensure we don't go past the end after resize
                  const maxTranslate = getMaxTranslate();
                  if (swiper.translate < maxTranslate) {
                    swiper.setTranslate(maxTranslate);
                    swiper.updateSlidesClasses();
                  }

                  // Update pagination after resize
                  updatePagination();
                }, 250);
              };
              window.addEventListener('resize', handleResize);

              // Also listen for Swiper's reachEnd event
              swiper.on('reachEnd', () => {
                const maxTranslate = getMaxTranslate();
                if (swiper.translate < maxTranslate) {
                  swiper.setTranslate(maxTranslate);
                  swiper.updateSlidesClasses();
                }
              });

              // Update after images load
              const images = swiper.el.querySelectorAll('img');
              let imagesLoaded = 0;
              const totalImages = images.length;

              if (totalImages > 0) {
                images.forEach((/** @type {HTMLImageElement} */ img) => {
                  if (img.complete) {
                    imagesLoaded++;
                  } else {
                    img.addEventListener('load', () => {
                      imagesLoaded++;
                      if (imagesLoaded === totalImages) {
                        swiper.update();
                        swiper.updateSlides();
                        swiper.updateSlidesClasses();
                        swiper.updateSize();
                        updatePagination();
                      }
                    });
                  }
                });

                if (imagesLoaded === totalImages) {
                  swiper.update();
                  swiper.updateSlides();
                  swiper.updateSlidesClasses();
                  swiper.updateSize();
                  updatePagination();
                }
              }
            }, 200);
          }
        }
      }, 200);
    }
  }

  #createProductCard(product) {
    const productUrl = `/products/${product.handle}`;
    const imageUrl = product.image?.url || '';
    const imageAlt = this.#escapeHtml(product.image?.altText || product.title);
    const formattedPrice = this.#formatPrice(product.price, product.currencyCode);
    const title = this.#escapeHtml(product.title);
    const highlights = this.#escapeHtml(product.highlights);
    const tag = product.tag ? this.#escapeHtml(product.tag) : '';


    let cardCon = document.createElement('div');

    cardCon.innerHTML = `
      <neo-product-card-1 class="neo-product-card-1"
        data-product-id="${product.productId}"
        data-enable-swiper="false"
        data-enable-pagination="false"
      >

      <div class="neo-product-card-details">

      <a class="title-container" href="${productUrl}">

      <h2 class="visually-hidden">${title}</h2>

      ${title ? `
      <p class="neo-product-card-title line-clamp-2 min-height">
      ${title}
      </p>
      ` : ``}

      ${highlights ? `
      <p class="neo-product-card-sub-title line-clamp-2 min-height">
      ${highlights}
      </p>
      ` : ``}




      </a>

      ${product.productId && product.variantId ? `
        <div class="neo-product-card-wishlist">
        <span
      class="smartwishlist"
      data-product="${product.productId}"
      data-variant="${product.variantId}"
      ></span>
      </div>
      ` : ``}

      </div>


      ${product.watchGifSrc ? `
        <div class="neo-product-card-1-watch-gif-container">
        <div class="relative">

        <img
        src="${product.watchGifSrc}"
        alt="${product.title}"
        class="neo-product-card-1-watch-gif"
      loading="lazy"
      />

      <svg class="neo-product-card-1-watch-play-icon" width="7" height="8" viewBox="0 0 7 8">
      <path d="M0 7.08734V0.735669C0 0.149096 0.653735 -0.200773 1.14179 0.124599L5.90983 3.30329C6.36248 3.60505 6.36248 4.27019 5.90983 4.57195L1.18526 7.72167C0.67862 8.05943 0 7.69624 0 7.08734Z" fill="white"/>
      </svg>

      </div>

      <p class="neo-product-card-1-watch-gif-text">Watch</p>

      </div>
      ` : ``}

      <div class="color-variant-images">
          <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
          <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
          <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
          <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
      </div>

      ${product.image?.url || product.imageTag || product.variantId ? `
        <div class="neo-product-card-media-container">

        ${product.imageTag ? `
        <p class="neo-product-card-new-tag">
        ${product.imageTag}
        </p>
      ` : ``}
        ${product.saleImageTag ? `
        <p class="neo-product-card-sale-tag">Sale</p>
      ` : ``}

      <div class="neo-product-card-media">

      ${product.image?.url ? `
      <div class="swiper">
      <div class="swiper-wrapper">

      <div class="swiper-slide">

      <a href="${productUrl}" class="neo-product-card-media--link">
      <img
      src="${product.image.url}"
      alt="${product.image.altText || product.title}"
      width="${product.image.width || ""}"
      height="${product.image.height || ""}"
      class="neo-product-card-1__image-img"
      />
      </a>
      </div>

      </div>
      </div>
      ` : ``}

      ${product.tag ? `
      <p class="neo-product-card-1-tag">
      ${product.tag}
      </p>
      ` : ``}

      </div>

      ${product.variantId ? `
      <button class="add-to-cart-button-gql  cursor-pointer" data-product-id="${product.productId}" data-variant-id="${product.variantId}" onclick="ATBModalOpen(event, '${product.productId}');
      document.querySelector('cart-drawer-component').close();">

      <span class="atc-link-button__corner atc-link-button__corner--tl">
        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
          <mask id="corner-mask-tl" fill="white">
          <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z" />
          </mask>
          <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#corner-mask-tl)" />
        </svg>
      </span>

      <span class="atc-link-button__corner atc-link-button__corner--tr">
        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
          <mask id="corner-mask-tl" fill="white">
          <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 0 0 7.10457 0 6V2Z" />
          </mask>
          <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#corner-mask-tl)" />
        </svg>
      </span>
      <span class="atc-link-button__corner atc-link-button__corner--bl">
        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
          <mask id="corner-mask-tl" fill="white">
          <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z" />
          </mask>
          <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#corner-mask-tl)" />
        </svg>
      </span>

      <span class="atc-link-button__corner atc-link-button__corner--br">
        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
          <mask id="corner-mask-tl" fill="white">
          <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 0 0 7.10457 0 6V2Z" />
          </mask>
          <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#corner-mask-tl)" />
        </svg>
      </span>
      ${window.AtcPriceBlock ? window.AtcPriceBlock.html(product.price, product.compareAtPrice) : `<span>${this.#formatPrice(product.price, product.currencyCode)}</span>`}
      <span>|</span>
      ${window.AtcPriceBlock && window.AtcPriceBlock.ctaPlusHtml ? window.AtcPriceBlock.ctaPlusHtml() : '<span class="add-to-cart-button-gql-plus"><span class="add-to-cart-button-gql-plus-icon">+</span><span class="add-to-cart-button-gql-label">Add</span></span>'}
      </button>
      ` : ``}

      </div>
      ` : ``}

      </neo-product-card-1>
      `;

    return cardCon;










    // const cornerSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none"><mask id="path-1-inside-1_6289_29312" fill="white"><path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z"/></mask><path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#path-1-inside-1_6289_29312)"/></svg>`;

    // card.innerHTML = `
    //   <div class="neo-product-card-details">
    //     <a class="title-container" href="${productUrl}">
    //       <h2 class="visually-hidden">${title}</h2>
    //       <p class="neo-product-card-title line-clamp-2 min-height">${title}</p>
    //       <p class="neo-product-card-sub-title line-clamp-2 min-height">${highlights}</p>
    //     </a>
    //     <div class="neo-product-card-wishlist">
    //       <span
    //         class="smartwishlist"
    //         data-product="${product.productId}"
    //         data-variant="${product.variantId}"
    //       ></span>
    //     </div>
    //   </div>

    //   <div class="neo-product-card-media-container">
    //     <div class="color-variant-images">
    //       <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
    //       <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
    //       <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
    //       <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
    //     </div>

    //     <div class="neo-product-card-media">
    //       <div class="swiper">
    //         <div class="swiper-wrapper">
    //           <div class="swiper-slide">
    //             <img
    //               src="${imageUrl}&width=520"
    //               alt="${imageAlt}"
    //               class="neo-product-card-1__image-img"
    //               loading="lazy"
    //               width="700"
    //             />
    //           </div>
    //         </div>
    //       </div>
    //       ${tag ? `<p class="neo-product-card-1-tag">${tag}</p>` : ''}
    //     </div>

    //     <button
    //       class="add-to-cart-button-gql"
    //       type="button"
    //       onclick="ATBModalOpen(event, '${product.productId}')"
    //       data-product-id="${product.productId}"
    //     >
    //       <span>${formattedPrice}</span>
    //       <span>|</span>
    //       <span class="add-to-cart-button-gql-plus">
    //         <span class="add-to-cart-button-gql-plus-icon">+</span>
    //         <span class="add-to-cart-button-gql-plus">
    //           <svg class="loading-svg" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><style>.spinner_aj0A{transform-origin:center;animation:spinner_KYSC .75s infinite linear}@keyframes spinner_KYSC{100%{transform:rotate(360deg)}}</style><path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z" class="spinner_aj0A"></path></svg>
    //         </span>
    //         Add to Cart
    //       </span>
    //       <div class="corner-1">${cornerSvg}</div>
    //       <div class="corner-2">${cornerSvg}</div>
    //       <div class="corner-3">${cornerSvg}</div>
    //       <div class="corner-4">${cornerSvg}</div>
    //     </button>
    //   </div>
    // `;

    return card;
  }

  #formatPrice(price) {
    const formatted = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);

    return `₹ ${formatted}`;
  }

  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  #hideSection() {
    this.style.display = 'none';
  }

  static addVariant(variantId) {
    if (!variantId) return;

    const storageKey = 'recentlyViewedVariants';
    const MAX_VARIANTS = 25;
    let variants = JSON.parse(localStorage.getItem(storageKey) || '[]');
    variants = variants.filter(id => id !== variantId);
    variants.unshift(variantId);
    variants = variants.slice(0, MAX_VARIANTS);
    localStorage.setItem(storageKey, JSON.stringify(variants));
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}


/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}

